/**
 * Behavior Tree Framework for AI decision making
 */

export type NodeStatus = 'success' | 'failure' | 'running';

export interface BehaviorContext {
  [key: string]: unknown;
}

/**
 * Base node interface for behavior tree
 */
export interface BehaviorNode<T extends BehaviorContext = BehaviorContext> {
  tick(context: T): NodeStatus;
  reset?(): void;
}

/**
 * Selector node - executes children in order until one succeeds
 */
export class Selector<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private children: BehaviorNode<T>[];
  private currentIndex: number = 0;

  constructor(children: BehaviorNode<T>[]) {
    this.children = children;
  }

  tick(context: T): NodeStatus {
    while (this.currentIndex < this.children.length) {
      const status = this.children[this.currentIndex].tick(context);

      if (status === 'success') {
        this.reset();
        return 'success';
      }

      if (status === 'running') {
        return 'running';
      }

      this.currentIndex++;
    }

    this.reset();
    return 'failure';
  }

  reset(): void {
    this.currentIndex = 0;
    this.children.forEach(child => child.reset?.());
  }
}

/**
 * Sequence node - executes children in order until one fails
 */
export class Sequence<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private children: BehaviorNode<T>[];
  private currentIndex: number = 0;

  constructor(children: BehaviorNode<T>[]) {
    this.children = children;
  }

  tick(context: T): NodeStatus {
    while (this.currentIndex < this.children.length) {
      const status = this.children[this.currentIndex].tick(context);

      if (status === 'failure') {
        this.reset();
        return 'failure';
      }

      if (status === 'running') {
        return 'running';
      }

      this.currentIndex++;
    }

    this.reset();
    return 'success';
  }

  reset(): void {
    this.currentIndex = 0;
    this.children.forEach(child => child.reset?.());
  }
}

/**
 * Parallel node - executes all children simultaneously
 */
export class Parallel<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private children: BehaviorNode<T>[];
  private successThreshold: number;
  private failureThreshold: number;

  constructor(
    children: BehaviorNode<T>[],
    successThreshold: number = children.length,
    failureThreshold: number = 1
  ) {
    this.children = children;
    this.successThreshold = successThreshold;
    this.failureThreshold = failureThreshold;
  }

  tick(context: T): NodeStatus {
    let successCount = 0;
    let failureCount = 0;

    for (const child of this.children) {
      const status = child.tick(context);

      if (status === 'success') successCount++;
      else if (status === 'failure') failureCount++;
    }

    if (successCount >= this.successThreshold) return 'success';
    if (failureCount >= this.failureThreshold) return 'failure';
    return 'running';
  }

  reset(): void {
    this.children.forEach(child => child.reset?.());
  }
}

/**
 * Condition node - evaluates a condition
 */
export class Condition<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private condition: (context: T) => boolean;

  constructor(condition: (context: T) => boolean) {
    this.condition = condition;
  }

  tick(context: T): NodeStatus {
    return this.condition(context) ? 'success' : 'failure';
  }
}

/**
 * Action node - executes an action
 */
export class Action<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private action: (context: T) => NodeStatus;

  constructor(action: (context: T) => NodeStatus) {
    this.action = action;
  }

  tick(context: T): NodeStatus {
    return this.action(context);
  }
}

/**
 * Inverter decorator - inverts the result of its child
 */
export class Inverter<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private child: BehaviorNode<T>;

  constructor(child: BehaviorNode<T>) {
    this.child = child;
  }

  tick(context: T): NodeStatus {
    const status = this.child.tick(context);
    if (status === 'success') return 'failure';
    if (status === 'failure') return 'success';
    return 'running';
  }

  reset(): void {
    this.child.reset?.();
  }
}

/**
 * Repeater decorator - repeats its child a specified number of times
 */
export class Repeater<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private child: BehaviorNode<T>;
  private maxCount: number;
  private currentCount: number = 0;

  constructor(child: BehaviorNode<T>, maxCount: number = Infinity) {
    this.child = child;
    this.maxCount = maxCount;
  }

  tick(context: T): NodeStatus {
    while (this.currentCount < this.maxCount) {
      const status = this.child.tick(context);

      if (status === 'running') return 'running';
      if (status === 'failure') {
        this.reset();
        return 'failure';
      }

      this.currentCount++;
      this.child.reset?.();
    }

    this.reset();
    return 'success';
  }

  reset(): void {
    this.currentCount = 0;
    this.child.reset?.();
  }
}

/**
 * Cooldown decorator - prevents child execution until cooldown expires
 */
export class Cooldown<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private child: BehaviorNode<T>;
  private cooldownTime: number;
  private lastExecutionTime: number = -Infinity;
  private getTime: () => number;

  constructor(child: BehaviorNode<T>, cooldownTime: number, getTime: () => number) {
    this.child = child;
    this.cooldownTime = cooldownTime;
    this.getTime = getTime;
  }

  tick(context: T): NodeStatus {
    const currentTime = this.getTime();
    if (currentTime - this.lastExecutionTime < this.cooldownTime) {
      return 'failure';
    }

    const status = this.child.tick(context);
    if (status === 'success') {
      this.lastExecutionTime = currentTime;
    }
    return status;
  }

  reset(): void {
    this.child.reset?.();
  }
}

/**
 * RandomSelector - selects a random child to execute
 */
export class RandomSelector<T extends BehaviorContext = BehaviorContext> implements BehaviorNode<T> {
  private children: BehaviorNode<T>[];
  private selectedIndex: number = -1;

  constructor(children: BehaviorNode<T>[]) {
    this.children = children;
  }

  tick(context: T): NodeStatus {
    if (this.selectedIndex === -1) {
      this.selectedIndex = Math.floor(Math.random() * this.children.length);
    }

    const status = this.children[this.selectedIndex].tick(context);

    if (status !== 'running') {
      this.reset();
    }

    return status;
  }

  reset(): void {
    this.selectedIndex = -1;
    this.children.forEach(child => child.reset?.());
  }
}
